// app/(app)/customers/[id].tsx (with keyboard fix)
import { Button, Input, TextArea } from "@/components/default/forms";
import SearchableSelect from "@/components/default/forms/SearchableSelect";
import { useAppTheme } from "@/hooks/useAppTheme";
import { Area, District, locationApi, State } from "@/services/locationApi";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  clearError,
  deleteCustomer,
  fetchCustomerById,
  resetSelected,
  updateCustomer,
} from "@/store/slices/customerSlice";
import { router, useFocusEffect, useLocalSearchParams } from "expo-router";
import { useCallback, useEffect, useState } from "react";
import {
  ActivityIndicator,
  Alert,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  View,
} from "react-native";

export default function EditCustomerScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { colors, spacing } = useAppTheme();
  const dispatch = useAppDispatch();
  const { selectedCustomer, loading, updating, error } = useAppSelector(
    (state) => state.customers,
  );

  const [fullName, setFullName] = useState("");
  // const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [gstin, setGstin] = useState("");
  const [address, setAddress] = useState("");
  const [pincode, setPincode] = useState("");
  const [status, setStatus] = useState<"active" | "inactive">("active");

  // Location
  const [states, setStates] = useState<State[]>([]);
  const [selectedStateId, setSelectedStateId] = useState<number | null>(null);
  const [districts, setDistricts] = useState<District[]>([]);
  const [selectedDistrictId, setSelectedDistrictId] = useState<number | null>(
    null,
  );
  const [areas, setAreas] = useState<Area[]>([]);
  const [selectedAreaId, setSelectedAreaId] = useState<number | null>(null);

  const [errors, setErrors] = useState({
    fullName: "",
    // email: "",
    phone: "",
    address: "",
    pincode: "",
    state: "",
    district: "",
    area: "",
  });

  useEffect(() => {
    dispatch(fetchCustomerById(Number(id)));
    return () => {
      dispatch(resetSelected());
    };
  }, [id]);

  useEffect(() => {
    if (selectedCustomer) {
      setFullName(selectedCustomer.fullName);
      // setEmail(selectedCustomer.email);
      setPhone(selectedCustomer.phone);
      setGstin(selectedCustomer.gstin || "");
      setAddress(selectedCustomer.address);
      setPincode(selectedCustomer.pincode);
      setStatus(selectedCustomer.status);
      setSelectedStateId(selectedCustomer.stateId);
      setSelectedDistrictId(selectedCustomer.districtId);
      setSelectedAreaId(selectedCustomer.areaId);
    }
  }, [selectedCustomer]);

  // Load static data
  useFocusEffect(
    useCallback(() => {
      locationApi.getStates().then(setStates);
      locationApi.getAreas().then(setAreas);
    }, []),
  );

  useEffect(() => {
    if (selectedStateId)
      locationApi.getDistrictsByState(selectedStateId).then(setDistricts);
    else setDistricts([]);
  }, [selectedStateId]);

  useEffect(() => {
    if (error) {
      Alert.alert("Error", error);
      dispatch(clearError());
    }
  }, [error]);

  const validate = () => {
    let valid = true;
    const newErrors = {
      fullName: "",
      // email: "",
      phone: "",
      address: "",
      pincode: "",
      state: "",
      district: "",
      area: "",
    };
    if (!fullName.trim()) {
      newErrors.fullName = "Required";
      valid = false;
    }
    // if (!email.trim()) {
    //   newErrors.email = "Required";
    //   valid = false;
    // } else if (!/\S+@\S+\.\S+/.test(email)) {
    //   newErrors.email = "Invalid email";
    //   valid = false;
    // }
    if (!phone.trim()) {
      newErrors.phone = "Required";
      valid = false;
    } else if (!/^\d{10}$/.test(phone)) {
      newErrors.phone = "10 digits required";
      valid = false;
    }
    if (!address.trim()) {
      newErrors.address = "Required";
      valid = false;
    }
    if (!pincode.trim()) {
      newErrors.pincode = "Required";
      valid = false;
    } else if (!/^\d{6}$/.test(pincode)) {
      newErrors.pincode = "6 digits required";
      valid = false;
    }
    if (!selectedStateId) newErrors.state = "Select state";
    if (!selectedDistrictId) newErrors.district = "Select district";
    if (!selectedAreaId) newErrors.area = "Select area";
    setErrors(newErrors);
    return valid;
  };

  const handleUpdate = async () => {
    if (!validate()) return;
    const result = await dispatch(
      updateCustomer({
        id: Number(id),
        data: {
          fullName,
          // email,
          phone,
          gstin: gstin || undefined,
          address,
          pincode,
          status,
          stateId: selectedStateId!,
          districtId: selectedDistrictId!,
          areaId: selectedAreaId!,
        },
      }),
    );
    if (updateCustomer.fulfilled.match(result))
      Alert.alert("Success", "Customer updated", [
        { text: "OK", onPress: () => router.back() },
      ]);
  };

  const handleDelete = () => {
    Alert.alert("Delete Customer", `Delete ${fullName}?`, [
      { text: "Cancel", style: "cancel" },
      {
        text: "Delete",
        style: "destructive",
        onPress: async () => {
          const result = await dispatch(deleteCustomer(Number(id)));
          if (deleteCustomer.fulfilled.match(result)) router.back();
        },
      },
    ]);
  };

  const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.background },
    keyboardAvoid: { flex: 1 },
    scrollContent: {
      flexGrow: 1,
      padding: spacing.lg,
      paddingBottom: spacing.xl,
    },
    loadingContainer: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
    },
    title: {
      fontSize: 24,
      fontWeight: "600",
      color: colors.text,
      marginBottom: spacing.xs,
    },
    idText: { fontSize: 14, color: colors.textLight, marginBottom: spacing.lg },
    inputContainer: { marginBottom: spacing.lg },
    statusContainer: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: spacing.lg,
      paddingVertical: spacing.sm,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    statusLabel: { fontSize: 16, fontWeight: "500", color: colors.text },
    buttonWrapper: { marginTop: "auto", paddingTop: spacing.lg },
    deleteButton: { marginTop: spacing.md },
  });

  if (loading)
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );

  return (
    <View style={styles.container}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={styles.keyboardAvoid}
        keyboardVerticalOffset={Platform.OS === "ios" ? 0 : 0}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          <Text style={styles.title}>Edit Customer</Text>
          <Text style={styles.idText}>ID: {id}</Text>
          <Input
            label="Full Name"
            value={fullName}
            onChangeText={setFullName}
            error={errors.fullName}
            containerStyle={styles.inputContainer}
          />
          {/* <Input
            label="Email"
            value={email}
            onChangeText={setEmail}
            keyboardType="email-address"
            autoCapitalize="none"
            error={errors.email}
            containerStyle={styles.inputContainer}
          /> */}
          <Input
            label="Phone Number"
            value={phone}
            onChangeText={setPhone}
            keyboardType="phone-pad"
            error={errors.phone}
            containerStyle={styles.inputContainer}
          />
          <Input
            label="GSTIN (Optional)"
            value={gstin}
            onChangeText={setGstin}
            containerStyle={styles.inputContainer}
          />

          <SearchableSelect
            label="State"
            options={states.map((s) => ({
              label: s.name,
              value: s.id.toString(),
            }))}
            value={selectedStateId?.toString()}
            onValueChange={(val) => setSelectedStateId(parseInt(val))}
            error={errors.state}
            containerStyle={styles.inputContainer}
          />
          {selectedStateId && (
            <SearchableSelect
              label="District"
              options={districts.map((d) => ({
                label: d.name,
                value: d.id.toString(),
              }))}
              value={selectedDistrictId?.toString()}
              onValueChange={(val) => setSelectedDistrictId(parseInt(val))}
              error={errors.district}
              containerStyle={styles.inputContainer}
            />
          )}
          <SearchableSelect
            label="Area / Root"
            options={areas.map((a) => ({
              label: a.name,
              value: a.id.toString(),
            }))}
            value={selectedAreaId?.toString()}
            onValueChange={(val) => setSelectedAreaId(parseInt(val))}
            error={errors.area}
            containerStyle={styles.inputContainer}
          />

          <Input
            label="Pincode"
            value={pincode}
            onChangeText={setPincode}
            keyboardType="numeric"
            error={errors.pincode}
            containerStyle={styles.inputContainer}
          />
          <TextArea
            label="Address"
            value={address}
            onChangeText={setAddress}
            numberOfLines={3}
            error={errors.address}
            containerStyle={styles.inputContainer}
          />

          <View style={styles.statusContainer}>
            <Text style={styles.statusLabel}>Status</Text>
            <Switch
              value={status === "active"}
              onValueChange={(val) => setStatus(val ? "active" : "inactive")}
              trackColor={{ false: colors.border, true: colors.primary }}
              thumbColor={colors.background}
            />
          </View>
          <View style={styles.buttonWrapper}>
            <Button
              title="Update Customer"
              onPress={handleUpdate}
              loading={updating}
            />
            <Button
              title="Delete Customer"
              onPress={handleDelete}
              variant="danger"
              containerStyle={styles.deleteButton}
            />
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}
